// src/pages/Contact.jsx
import { motion } from "framer-motion";
import {
  FaEnvelope,
  FaFacebookF,
  FaGlobe,
  FaInstagram,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import {
  HiArrowRight,
  HiArrowUpRight,
  HiCheckCircle,
  HiChatBubbleLeftRight,
  HiSparkles,
} from "react-icons/hi2";
import ContactForm from "../components/ContactForm";

const contactInfo = [
  {
    icon: <FaMapMarkerAlt />,
    label: "Address",
    text: "#23-7-168, Santhi Nagar, M.R.Palli Circle, Tirupati – 517502",
    href: "https://www.google.com/maps/search/?api=1&query=Innovative+Academy+for+IT+Solutions+Tirupati",
  },
  {
    icon: <FaPhoneAlt />,
    label: "Phone",
    text: "8332029863 | 8008638374",
    href: "tel:8332029863",
  },
  {
    icon: <FaEnvelope />,
    label: "Email",
    text: "hr@ia4solutions.com",
    href: "mailto:hr@ia4solutions.com",
  },
  {
    icon: <FaGlobe />,
    label: "Website",
    text: "www.ia4solutions.com",
    href: "https://www.ia4solutions.com",
  },
];

const socialLinks = [
  {
    name: "Facebook",
    icon: FaFacebookF,
    href: "https://www.facebook.com/people/Innovative-Academy-For-It-Solutions/61567177282782/",
    className: "hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 dark:hover:border-blue-900 dark:hover:bg-blue-950/40 dark:hover:text-blue-400",
  },
  {
    name: "LinkedIn",
    icon: FaLinkedinIn,
    href: "https://www.linkedin.com/company/ia4solutions/",
    className: "hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600 dark:hover:border-sky-900 dark:hover:bg-sky-950/40 dark:hover:text-sky-400",
  },
  {
    name: "Instagram",
    icon: FaInstagram,
    href: "https://www.instagram.com/innovativeacademyitsolutions/",
    className: "hover:border-pink-200 hover:bg-pink-50 hover:text-pink-600 dark:hover:border-pink-900 dark:hover:bg-pink-950/40 dark:hover:text-pink-400",
  },
  {
    name: "X",
    icon: FaTwitter,
    href: "https://x.com/ia4solution",
    className: "hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 dark:hover:border-gray-700 dark:hover:bg-gray-800 dark:hover:text-white",
  },
  {
    name: "YouTube",
    icon: FaYoutube,
    href: "https://www.youtube.com/@ia4solutions",
    className: "hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:hover:border-red-900 dark:hover:bg-red-950/40 dark:hover:text-red-400",
  },
];

const Contact = () => {
  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f7f9fc] text-gray-950 dark:bg-[#070b14] dark:text-white">
      {/* =========================================================
          GLOBAL ATMOSPHERE
      ========================================================== */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-260px] h-[620px] w-[980px] -translate-x-1/2 rounded-full bg-indigo-500/[0.06] blur-[140px] dark:bg-indigo-500/[0.09]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-[520px] h-[360px] w-[360px] rounded-full bg-cyan-400/[0.04] blur-[120px] dark:bg-cyan-400/[0.05]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 top-[920px] h-[380px] w-[380px] rounded-full bg-violet-500/[0.035] blur-[120px] dark:bg-violet-500/[0.05]"
      />

      {/* =========================================================
          HERO
      ========================================================== */}
      <section className="relative px-4 pb-10 pt-16 sm:px-6 sm:pb-12 sm:pt-20 lg:px-8 lg:pb-16 lg:pt-24">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="mb-6 flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.17em] text-gray-400 dark:text-gray-500"
          >
            <span>IA4Solutions</span>
            <span aria-hidden="true" className="text-gray-300 dark:text-gray-700">
              /
            </span>
            <span className="text-indigo-600 dark:text-indigo-400">Contact</span>
          </motion.div>

          <div className="grid items-end gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05, ease: "easeOut" }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-white/80 px-3 py-1.5 text-[8px] font-bold uppercase tracking-[0.17em] text-indigo-600 shadow-sm backdrop-blur dark:border-indigo-900/50 dark:bg-gray-900/70 dark:text-indigo-400">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 shadow-[0_0_0_4px_rgba(99,102,241,0.10)]" />
                Let's connect
              </div>

              <h1 className="mt-5 max-w-4xl text-[2.8rem] font-black leading-[0.95] tracking-[-0.065em] text-gray-950 sm:text-5xl lg:text-[4.7rem] dark:text-white">
                One message.
                <br />
                <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  The right next step.
                </span>
              </h1>

              <p className="mt-6 max-w-2xl text-sm leading-7 text-gray-600 sm:text-[15px] dark:text-gray-300">
                Have a question, project idea, or partnership opportunity with{" "}
                <strong className="font-semibold text-gray-900 dark:text-white">
                  Innovative Academy for IT Solutions
                </strong>
                ? Tell us what you are working toward, and our team can help you
                take the next step.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a
                  href="#contact-form"
                  className="group inline-flex items-center gap-2 rounded-xl bg-gray-950 px-5 py-3 text-[11px] font-bold text-white shadow-[0_12px_30px_rgba(15,23,42,0.13)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-indigo-600 dark:bg-white dark:text-gray-950 dark:hover:bg-indigo-500 dark:hover:text-white"
                >
                  Start a Conversation
                  <HiArrowRight className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </a>

                <a
                  href="#contact-details"
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white/80 px-5 py-3 text-[11px] font-bold text-gray-700 shadow-sm backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-600 dark:border-gray-800 dark:bg-gray-900/70 dark:text-gray-200 dark:hover:border-indigo-800 dark:hover:text-indigo-400"
                >
                  View Contact Details
                  <HiArrowRight className="text-sm" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-[28px] border border-gray-200/80 bg-white/80 p-5 shadow-[0_22px_70px_rgba(15,23,42,0.07)] backdrop-blur-xl dark:border-gray-800 dark:bg-gray-900/75 dark:shadow-[0_22px_70px_rgba(0,0,0,0.28)] sm:p-6">
                <div
                  aria-hidden="true"
                  className="absolute right-[-55px] top-[-55px] h-44 w-44 rounded-full bg-indigo-500/[0.09] blur-3xl dark:bg-indigo-500/[0.12]"
                />

                <div className="relative z-10">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-400">
                        Connection Desk
                      </p>
                      <p className="mt-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                        Reach the team through the channel that works for you.
                      </p>
                    </div>

                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-indigo-400">
                      <HiChatBubbleLeftRight className="text-base" />
                    </div>
                  </div>

                  <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
                    {[
                      ["01", "Ask", "Questions & guidance"],
                      ["02", "Build", "Project discussions"],
                      ["03", "Grow", "Training & careers"],
                      ["04", "Partner", "Business opportunities"],
                    ].map(([number, title, text]) => (
                      <div
                        key={number}
                        className="rounded-2xl border border-gray-200/80 bg-gray-50/80 px-4 py-3.5 dark:border-gray-800 dark:bg-gray-950/60"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-[8px] font-bold uppercase tracking-[0.14em] text-gray-400">
                            {number}
                          </span>
                          <span className="text-sm font-extrabold tracking-[-0.02em] text-gray-900 dark:text-white">
                            {title}
                          </span>
                        </div>
                        <p className="mt-1 text-[9px] font-medium text-gray-500 dark:text-gray-400">
                          {text}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 dark:border-emerald-900/50 dark:bg-emerald-950/25">
                    <HiCheckCircle className="text-base text-emerald-500" />
                    <span className="text-[9px] font-semibold text-emerald-800 dark:text-emerald-200">
                      Clear communication. Practical direction. One place to start.
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* =========================================================
          CONTACT WORKSPACE
      ========================================================== */}
      <section className="relative px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8 lg:pb-24">
        <div className="mx-auto grid max-w-7xl items-stretch gap-6 lg:grid-cols-[1.08fr_0.92fr]">
          {/* FORM */}
          <motion.div
            id="contact-form"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-24 rounded-[28px] border border-gray-200/80 bg-white p-5 shadow-[0_20px_65px_rgba(15,23,42,0.065)] dark:border-gray-800 dark:bg-gray-900/85 dark:shadow-[0_20px_65px_rgba(0,0,0,0.24)] sm:p-7 lg:p-8"
          >
            <div className="mb-7 flex items-start justify-between gap-4 border-b border-gray-100 pb-6 dark:border-gray-800">
              <div>
                <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-400">
                  Send an enquiry
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-[-0.04em] text-gray-950 sm:text-3xl dark:text-white">
                  Tell us what you need.
                </h2>
                <p className="mt-2 max-w-xl text-xs leading-6 text-gray-500 sm:text-sm dark:text-gray-400">
                  Share the details with our team through the form below.
                </p>
              </div>

              <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-600 sm:flex dark:border-indigo-900/50 dark:bg-indigo-950/30 dark:text-indigo-400">
                <HiSparkles className="text-base" />
              </div>
            </div>

            <ContactForm />
          </motion.div>

          {/* DETAILS */}
          <motion.div
            id="contact-details"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="scroll-mt-24 rounded-[28px] border border-gray-200/80 bg-white p-5 shadow-[0_20px_65px_rgba(15,23,42,0.065)] dark:border-gray-800 dark:bg-gray-900/85 dark:shadow-[0_20px_65px_rgba(0,0,0,0.24)] sm:p-7 lg:p-8"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-400">
                  Direct channels
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-[-0.04em] text-gray-950 sm:text-3xl dark:text-white">
                  Contact information
                </h2>
              </div>

              <div className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-[8px] font-bold uppercase tracking-[0.14em] text-gray-500 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400">
                Tirupati
              </div>
            </div>

            <div className="mt-7 divide-y divide-gray-100 dark:divide-gray-800">
              {contactInfo.map((item) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  target={item.label === "Address" || item.label === "Website" ? "_blank" : undefined}
                  rel={item.label === "Address" || item.label === "Website" ? "noopener noreferrer" : undefined}
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.2 }}
                  className="group flex items-start gap-4 py-4 first:pt-0 last:pb-0"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-indigo-500 transition-all duration-200 group-hover:border-indigo-200 group-hover:bg-indigo-50 group-hover:text-indigo-600 dark:border-gray-800 dark:bg-gray-950 dark:text-indigo-400 dark:group-hover:border-indigo-800 dark:group-hover:bg-indigo-950/40">
                    <span className="text-base">{item.icon}</span>
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
                        {item.label}
                      </span>
                      <HiArrowUpRight className="text-xs text-gray-300 transition-colors group-hover:text-indigo-400 dark:text-gray-600" />
                    </span>
                    <span className="mt-1 block break-words text-sm font-semibold leading-6 text-gray-800 dark:text-gray-200">
                      {item.text}
                    </span>
                  </span>
                </motion.a>
              ))}
            </div>

            {/* SOCIALS */}
            <div className="mt-8 border-t border-gray-100 pt-7 dark:border-gray-800">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-gray-400">
                    Stay connected
                  </p>
                  <h3 className="mt-1.5 text-lg font-black tracking-[-0.025em] text-gray-900 dark:text-white">
                    Follow IA4Solutions
                  </h3>
                </div>
                <span className="hidden text-[9px] font-medium text-gray-400 sm:block">
                  Social channels
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2.5">
                {socialLinks.map((social) => {
                  const SocialIcon = social.icon;

                  return (
                    <motion.a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Follow IA4Solutions on ${social.name}`}
                      whileHover={{ y: -2 }}
                      transition={{ duration: 0.2 }}
                      className={`flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition-all duration-200 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400 ${social.className}`}
                    >
                      <SocialIcon className="text-sm" />
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* =========================================================
          LOCATION
      ========================================================== */}
      <section className="relative border-t border-gray-200/70 bg-white/60 px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-20 dark:border-gray-800 dark:bg-gray-950/45">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-400">
                Find us
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-[-0.04em] text-gray-950 sm:text-3xl dark:text-white">
                Our location in Tirupati.
              </h2>
              <p className="mt-2 max-w-2xl text-xs leading-6 text-gray-500 sm:text-sm dark:text-gray-400">
                Visit us at our Tirupati location or reach out through any of the
                direct channels above.
              </p>
            </div>

            <a
              href={contactInfo[0].href}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-fit items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[10px] font-bold text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-600 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-indigo-800 dark:hover:text-indigo-400"
            >
              Open in Google Maps
              <HiArrowUpRight className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </a>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.45 }}
            className="mt-7 overflow-hidden rounded-[26px] border border-gray-200/80 bg-white shadow-[0_20px_65px_rgba(15,23,42,0.06)] dark:border-gray-800 dark:bg-gray-900 dark:shadow-[0_20px_65px_rgba(0,0,0,0.24)]"
          >
            <iframe
              title="IA4Solutions Location"
              src="https://www.google.com/maps?q=Innovative+Academy+for+IT+Solutions+Tirupati&output=embed"
              width="100%"
              height="380"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </section>

      {/* =========================================================
          FINAL CTA
      ========================================================== */}
      <section className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="relative mx-auto max-w-6xl overflow-hidden rounded-[28px] bg-gray-950 px-6 py-10 text-center shadow-[0_24px_70px_rgba(15,23,42,0.13)] sm:px-10 sm:py-14 lg:px-14 lg:py-16 dark:bg-black"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-[-100px] h-72 w-[520px] -translate-x-1/2 rounded-full bg-indigo-500/20 blur-[105px]"
          />

          <div className="relative z-10">
            <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-cyan-300">
              Ready when you are
            </p>

            <h2 className="mx-auto mt-3 max-w-3xl text-3xl font-black leading-tight tracking-[-0.045em] text-white sm:text-4xl lg:text-5xl">
              Have something in mind?
              <span className="text-indigo-300"> Let&apos;s start there.</span>
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-white/60">
              Whether it is a question, a project idea, or a partnership
              opportunity, one conversation can create the next step.
            </p>

            <div className="mt-7">
              <a
                href="#contact-form"
                className="group inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-[11px] font-bold text-gray-950 shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-indigo-50"
              >
                Send an Enquiry
                <HiArrowRight className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Contact;
